import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    closeLimit,
    mediaFileTypes,
    minCountMediaFiles,
    PROMO_ID,
    showInterval,
    showLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/aiGenerateAndEditMediaContentFakedoor';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { groupedIds } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';
import { isFakedoorCompleted } from 'reactApp/utils/aiGenerateAndEditFakedoor';

export class BannerModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.querySelector('#banners-above-toolbar');
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/AiGenerateAndEditFakedoor/AiGenerateAndEditFakedoorBanner'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiGenerateAndEditFakedoorCreator: ModelCreator = ({ models, render, stores }) =>
    new BannerModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit,
            closeLimit,
            showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const isBizUser = UserSelectors.isBizUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);
                const storage = getCurrentStorage(state);

                // Закомплитили фейкдор через промо-баннер или через иконки с тултипом
                if (isFakedoorCompleted()) {
                    reject();
                    return;
                }

                // Только десктоп
                if (IS_PHONE_BROWSER) {
                    reject();
                    return;
                }

                // Не показываем бизам, платникам и оверквотерам
                if (isBizUser || isPaidUser || isOverquota) {
                    reject();
                    return;
                }

                // Показываем только в хомяке и в галерее
                if (!(storage === EStorageType.home || storage === EStorageType.gallery)) {
                    reject();
                    return;
                }

                // Под фичей проверяем минимальное количество медиа-файлов
                if (minCountMediaFiles) {
                    const files = groupedIds(state, storage, false);
                    const mediaFiles = files.filter((file: string) =>
                        mediaFileTypes?.some?.((extansion) => file.endsWith(`.${extansion}`))
                    );

                    if (mediaFiles.length < minCountMediaFiles) {
                        reject();
                        return;
                    }
                }

                resolve();
            },
        })
    );
